import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Minimal fixed-row-height windowing utility (a from-scratch alternative
 * to react-window/react-virtual). It only computes and renders the slice
 * of `itemCount` rows currently intersecting the scroll container's
 * viewport, plus a small overscan buffer, so a library of 1000+ fonts
 * never mounts more than a couple dozen FontCard DOM subtrees at once.
 *
 * Usage:
 *   const { containerRef, virtualItems, totalHeight } =
 *     useVirtualList({ itemCount, itemHeight });
 *
 *   <div ref={containerRef} style={{ overflowY: 'auto' }}>
 *     <div style={{ height: totalHeight, position: 'relative' }}>
 *       {virtualItems.map(({ index, offsetTop }) => (
 *         <div key={index} style={{ position: 'absolute', top: offsetTop, width: '100%' }}>
 *           <Row index={index} />
 *         </div>
 *       ))}
 *     </div>
 *   </div>
 */
export function useVirtualList({ itemCount, itemHeight, overscan = 4 }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    setViewportHeight(el.clientHeight);

    let rafId = null;
    const handleScroll = () => {
      // rAF-throttle scroll handling so we never do more than one layout
      // pass per frame, keeping scrolling smooth even on long lists.
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setScrollTop(el.scrollTop);
        rafId = null;
      });
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportHeight(entry.contentRect.height);
      }
    });

    el.addEventListener('scroll', handleScroll, { passive: true });
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const totalHeight = itemCount * itemHeight;

  const firstVisible = Math.floor(scrollTop / itemHeight);
  const lastVisible = Math.ceil((scrollTop + viewportHeight) / itemHeight);

  const startIndex = Math.max(0, firstVisible - overscan);
  const endIndex = Math.min(itemCount - 1, lastVisible + overscan);

  const virtualItems = [];
  for (let index = startIndex; index <= endIndex; index += 1) {
    virtualItems.push({ index, offsetTop: index * itemHeight });
  }

  const scrollToIndex = useCallback(
    (index) => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({ top: index * itemHeight, behavior: 'smooth' });
    },
    [itemHeight]
  );

  return { containerRef, virtualItems, totalHeight, scrollToIndex };
}
